import { useQuery } from "@tanstack/react-query";
import Client from "shopify-buy";
// NEW: read market so queryKey changes when user switches country and pass to fetchers
import { useMarket } from "../context/MarketContext.jsx";

// Initialize Shopify client
const client = Client.buildClient({
  storefrontAccessToken: "64d3395ffdbadbcad4398c2e893d8a76",
  domain: "b3deb6-2.myshopify.com",
});

/* ------------------------- GID helpers & constants ------------------------- */

// Base64 helper (browser or Node/SSR)
const toBase64 = (s) => {
  try {
    return btoa(s);
  } catch (e) {
    if (typeof Buffer !== "undefined") return Buffer.from(s).toString("base64");
    return s;
  }
};

// Construct the global (base64) Collection ID Shopify GraphQL expects
const constructCollectionId = (numericId) =>
  toBase64(`gid://shopify/Collection/${numericId}`);

// Shop All collection (from your admin URL)
const SHOP_ALL_NUMERIC_ID = "459894325565";
const SHOP_ALL_GID = constructCollectionId(SHOP_ALL_NUMERIC_ID);

/* --------------------- Collection mapping (numeric IDs) -------------------- */
const collectionIdMap = {
  bras: ["459578573117"],
  knickers: ["673627898237"],
  swim: ["459576869181"],
  clothing: ["465259266365"],
  accessories: ["677295784317"],
  personalised: ["459893539133"],
};

/* ------------------- Fetch all (from Shop All collection) ------------------
   NOTE: now accepts `country` and uses Shopify's @inContext directive placed
   after the variable list (correct GraphQL syntax).
*/
const fetchAllProducts = async (country = "GB") => {
  const allProducts = [];
  let hasNextPage = true;
  let endCursor = null;

  // ensure country is safe (ISO2 uppercase)
  const ctxCountry = (country || "GB").toUpperCase();

  while (hasNextPage) {
    // Variables first, then directive
    const query = `
      query ($id: ID!, $cursor: String) @inContext(country: ${ctxCountry}) {
        collection(id: $id) {
          products(first: 20, after: $cursor, sortKey: COLLECTION_DEFAULT) {
            edges {
              node {
                id
                handle
                title
                description
                availableForSale
                images(first: 2) {
                  edges { node {
                    url(transform: { preferredContentType: WEBP, maxWidth: 1200 })
                    width
                    height
                  } }
                }
                variants(first: 1) {
                  edges {
                    node {
                      id
                      availableForSale
                      price { amount currencyCode }
                      compareAtPrice { amount currencyCode }
                    }
                  }
                }
              }
            }
            pageInfo { hasNextPage endCursor }
          }
        }
      }
    `;

    const res = await fetch(
      `https://${client.config.domain}/api/2024-07/graphql`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token":
            client.config.storefrontAccessToken,
        },
        body: JSON.stringify({
          query,
          variables: { id: SHOP_ALL_GID, cursor: endCursor },
        }),
      },
    );

    const json = await res.json();
    const conn = json?.data?.collection?.products;
    if (!conn) return []; // handle missing collection

    allProducts.push(...conn.edges.map((e) => e.node));
    hasNextPage = conn.pageInfo.hasNextPage;
    endCursor = conn.pageInfo.endCursor;
  }

  return allProducts;
};

/* --------------- Fetch specific types (mapped collections) -----------------
   NOTE: accepts `country` and uses @inContext after the variable list.
*/
const fetchProductsByType = async (filter, country = "GB") => {
  const numericIds = collectionIdMap[filter.toLowerCase()] || [];
  if (numericIds.length === 0) return [];

  const allFilteredProducts = [];
  const ctxCountry = (country || "GB").toUpperCase();

  for (const numericId of numericIds) {
    const collectionId = constructCollectionId(numericId);
    let hasNextPage = true;
    let endCursor = null;

    while (hasNextPage) {
      const query = `
        query($collectionId: ID!, $cursor: String) @inContext(country: ${ctxCountry}) {
          collection(id: $collectionId) {
            products(first: 20, after: $cursor, sortKey: COLLECTION_DEFAULT) {
              edges {
                node {
                  id
                  handle
                  title
                  description
                  availableForSale
                  images(first: 2) {
                    edges { node {
                      url(transform: { preferredContentType: WEBP, maxWidth: 1200 })
                      width
                      height
                    } }
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        availableForSale
                        price { amount currencyCode }
                        compareAtPrice { amount currencyCode }
                      }
                    }
                  }
                }
              }
              pageInfo { hasNextPage endCursor }
            }
          }
        }
      `;

      const res = await fetch(
        `https://${client.config.domain}/api/2024-07/graphql`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token":
              client.config.storefrontAccessToken,
          },
          body: JSON.stringify({
            query,
            variables: { collectionId, cursor: endCursor },
          }),
        },
      );

      const json = await res.json();
      const conn = json?.data?.collection?.products;
      if (!conn) break;

      allFilteredProducts.push(...conn.edges.map((e) => e.node));
      hasNextPage = conn.pageInfo.hasNextPage;
      endCursor = conn.pageInfo.endCursor;
    }
  }

  return allFilteredProducts;
};

/* -------------------------- React Query hook ------------------------------- */
export const useProductsQuery = (filter) => {
  // include market.country so queries refetch when user switches market
  const { market } = useMarket();
  const country = (market?.country ?? "GB").toUpperCase();

  const key = ["products", filter ?? "all", country];

  return useQuery({
    queryKey: key,
    queryFn: () =>
      filter && filter !== "all"
        ? fetchProductsByType(filter, country)
        : fetchAllProducts(country),
    placeholderData: (prev) => prev,
  });
};
